/**
 * Shared ReactMarkdown components - used by AnalysisSteps and KeyFindings for consistent markdown rendering
 */
import type { Components } from 'react-markdown';
import HighlightBadge from '../atoms/HighlightBadge/HighlightBadge';

/** Fix unclosed color spans from API, e.g. (<span style="color:red">-2.95%) -> (<span style="color:red">-2.95%</span>) */
export function fixUnclosedColorSpans(text: string): string {
    return text.replace(
        /(<span\s+[^>]*style="[^"]*color\s*:\s*(?:red|green)[^"]*"[^>]*>)([^<]+)(\))/g,
        (_, open, content, paren) => open + content + '</span>' + paren
    );
}

export const extractTextContent = (children: any): string => {
    if (typeof children === 'string') {
        return children;
    }
    if (typeof children === 'number') {
        return String(children);
    }
    if (Array.isArray(children)) {
        return children.map(extractTextContent).join('');
    }
    if (children && typeof children === 'object' && 'props' in children) {
        return extractTextContent(children.props?.children);
    }
    return '';
};

export const markdownComponents: Components = {
    h1: ({ node, ...props }) => (
        <h1
            className="font-['Open_Sans'] font-bold text-[20px] leading-[28px] text-[rgba(0,0,0,0.65)] mb-3 mt-4 first:mt-0"
            {...props}
        />
    ),
    h2: ({ node, ...props }) => (
        <h2
            className="font-['Open_Sans'] font-bold text-[18px] leading-[26px] text-[rgba(0,0,0,0.65)] mb-3 mt-4 first:mt-0"
            {...props}
        />
    ),
    h3: ({ node, ...props }) => (
        <h3
            className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.65)] mb-2 mt-3 first:mt-0"
            {...props}
        />
    ),
    h4: ({ node, ...props }) => (
        <h4
            className="font-['Open_Sans'] font-bold text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] mb-2 mt-3 first:mt-0"
            {...props}
        />
    ),
    h5: ({ node, ...props }) => (
        <h5
            className="font-['Open_Sans'] font-bold text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] mb-2 mt-2 first:mt-0"
            {...props}
        />
    ),
    h6: ({ node, ...props }) => (
        <h6
            className="font-['Open_Sans'] font-bold text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] mb-2 mt-2 first:mt-0"
            {...props}
        />
    ),
    p: ({ node, ...props }) => (
        <p
            className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] mb-2 m-0"
            {...props}
        />
    ),
    ul: ({ node, ...props }) => (
        <ul
            className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] mb-2 ml-4 list-disc"
            {...props}
        />
    ),
    ol: ({ node, ...props }) => (
        <ol
            className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] mb-2 ml-4 list-decimal"
            {...props}
        />
    ),
    li: ({ node, ...props }) => <li className="mb-1" {...props} />,
    table: ({ node, ...props }) => (
        <div className="overflow-x-auto my-3">
            <table
                className="min-w-full border-collapse border border-[rgba(0,0,0,0.06)]"
                {...props}
            />
        </div>
    ),
    thead: ({ node, ...props }) => <thead className="bg-[rgba(0,0,0,0.02)]" {...props} />,
    tbody: ({ node, ...props }) => <tbody {...props} />,
    tr: ({ node, ...props }) => (
        <tr className="border-b border-[rgba(0,0,0,0.06)]" {...props} />
    ),
    th: ({ node, ...props }) => (
        <th
            className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] px-3 py-2 text-left border border-[rgba(0,0,0,0.06)]"
            {...props}
        />
    ),
    td: ({ node, ...props }) => (
        <td
            className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] px-3 py-2 border border-[rgba(0,0,0,0.06)]"
            {...props}
        />
    ),
    span: ({ node, ...props }: any) => {
        const styleAttr = (node?.properties as any)?.style ?? (props as any).style;
        const children = props.children;
        const textContent = extractTextContent(children);

        // Normalize color: may be string "color:red", "color: green", or object { color: "red" }
        let colorValue: string | undefined;
        if (typeof styleAttr === 'string') {
            const match = styleAttr.match(/color\s*:\s*([^;]+)/i);
            if (match) colorValue = match[1].trim();
        } else if (styleAttr && typeof styleAttr === 'object' && 'color' in styleAttr) {
            colorValue = String(styleAttr.color);
        }

        if (colorValue) {
            const isRed = /^(red|#ec343e|#ff0000|#f00|rgb\s*\(\s*255\s*,?\s*0\s*,?\s*0\s*\))$/i.test(colorValue.trim());
            const isGreen = /^(green|#00a63e|#389e0d|#0f0|rgb\s*\(\s*0\s*,?\s*166\s*,?\s*62\s*\))$/i.test(colorValue.trim());
            if (isRed) return <HighlightBadge value={textContent.trim()} type="negative" />;
            if (isGreen) return <HighlightBadge value={textContent.trim()} type="positive" />;
        }

        return <span {...props} />;
    },
    code: ({ node, ...props }: any) => {
        const isInline = (props as any).inline;
        const codeContent = String(props.children || '');

        const badgeMatch = codeContent.match(/^badge:(positive|negative):(.+)$/);
        if (badgeMatch && isInline) {
            const badgeType = badgeMatch[1] as 'positive' | 'negative';
            const badgeValue = badgeMatch[2];
            return <HighlightBadge value={badgeValue} type={badgeType} />;
        }

        return isInline ? (
            <code
                className="bg-[rgba(0,0,0,0.06)] px-1 py-0.5 rounded text-[13px] font-mono"
                {...props}
            />
        ) : (
            <code
                className="block bg-[rgba(0,0,0,0.06)] p-3 rounded my-2 text-[13px] font-mono overflow-x-auto"
                {...props}
            />
        );
    },
    pre: ({ node, ...props }) => (
        <pre className="bg-[rgba(0,0,0,0.06)] p-3 rounded my-2 overflow-x-auto" {...props} />
    ),
    blockquote: ({ node, ...props }) => (
        <blockquote
            className="border-l-4 border-[rgba(0,0,0,0.2)] pl-4 my-2 italic text-[rgba(0,0,0,0.65)]"
            {...props}
        />
    ),
    strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
    em: ({ node, ...props }) => <em className="italic" {...props} />,
};

/**
 * Agentic-style markdown components - matches AgenticLeftPanel summary text styling
 * (16px font, text-[rgba(0,0,0,0.88)], tables, HighlightBadge for color spans/badge:positive|negative)
 * Use in Key Takeaways (right panel), Phase overview, and similar agentic UI.
 */
export const agenticMarkdownComponents: Components = {
    h1: ({ node, ...props }) => (
        <h1
            className="font-['Open_Sans'] font-bold text-[20px] leading-[28px] text-[rgba(0,0,0,0.88)] mb-3 mt-4 first:mt-0"
            {...props}
        />
    ),
    h2: ({ node, ...props }) => (
        <h2
            className="font-['Open_Sans'] font-bold text-[18px] leading-[26px] text-[rgba(0,0,0,0.88)] mb-3 mt-4 first:mt-0"
            {...props}
        />
    ),
    h3: ({ node, ...props }) => (
        <h3
            className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-3 first:mt-0"
            {...props}
        />
    ),
    h4: ({ node, ...props }) => (
        <h4
            className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-3 first:mt-0"
            {...props}
        />
    ),
    h5: ({ node, ...props }) => (
        <h5
            className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-2 first:mt-0"
            {...props}
        />
    ),
    h6: ({ node, ...props }) => (
        <h6
            className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-2 first:mt-0"
            {...props}
        />
    ),
    p: ({ node, ...props }) => (
        <p
            className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 m-0"
            {...props}
        />
    ),
    ul: ({ node, ...props }) => (
        <ul
            className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 ml-4 list-disc"
            {...props}
        />
    ),
    ol: ({ node, ...props }) => (
        <ol
            className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 ml-4 list-decimal"
            {...props}
        />
    ),
    li: ({ node, ...props }) => <li className="mb-1" {...props} />,
    table: ({ node, ...props }) => (
        <div className="overflow-x-auto my-3">
            <table
                className="min-w-full border-collapse border border-[rgba(0,0,0,0.06)]"
                {...props}
            />
        </div>
    ),
    thead: ({ node, ...props }) => (
        <thead className="bg-[rgba(0,0,0,0.02)]" {...props} />
    ),
    tbody: ({ node, ...props }) => <tbody {...props} />,
    tr: ({ node, ...props }) => (
        <tr className="border-b border-[rgba(0,0,0,0.06)]" {...props} />
    ),
    th: ({ node, ...props }) => (
        <th
            className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-3 py-2 text-left border border-[rgba(0,0,0,0.06)]"
            {...props}
        />
    ),
    td: ({ node, ...props }) => (
        <td
            className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-3 py-2 border border-[rgba(0,0,0,0.06)]"
            {...props}
        />
    ),
    span: ({ node, ...props }: any) => {
        const styleAttr = (node?.properties as any)?.style ?? (props as any).style;
        const children = props.children;
        const textContent = extractTextContent(children);

        let colorValue: string | undefined;
        if (typeof styleAttr === 'string') {
            const match = styleAttr.match(/color\s*:\s*([^;]+)/i);
            if (match) colorValue = match[1].trim();
        } else if (styleAttr && typeof styleAttr === 'object' && 'color' in styleAttr) {
            colorValue = String(styleAttr.color);
        }

        if (colorValue) {
            const isRed =
                /^(red|#ec343e|#ff0000|#f00|rgb\s*\(\s*255\s*,?\s*0\s*,?\s*0\s*\))$/i.test(colorValue.trim());
            const isGreen =
                /^(green|#00a63e|#389e0d|#0f0|rgb\s*\(\s*0\s*,?\s*166\s*,?\s*62\s*\))$/i.test(colorValue.trim());
            if (isRed) return <HighlightBadge value={textContent.trim()} type="negative" />;
            if (isGreen) return <HighlightBadge value={textContent.trim()} type="positive" />;
        }

        return <span {...props} />;
    },
    code: ({ node, ...props }: any) => {
        const isInline = (props as any).inline;
        const codeContent = String(props.children || '');

        const badgeMatch = codeContent.match(/^badge:(positive|negative):(.+)$/);
        if (badgeMatch && isInline) {
            const badgeType = badgeMatch[1] as 'positive' | 'negative';
            const badgeValue = badgeMatch[2];
            return <HighlightBadge value={badgeValue} type={badgeType} />;
        }

        return isInline ? (
            <code
                className="bg-[rgba(0,0,0,0.06)] px-1 py-0.5 rounded text-[13px] font-mono"
                {...props}
            />
        ) : (
            <code
                className="block bg-[rgba(0,0,0,0.06)] p-3 rounded my-2 text-[13px] font-mono overflow-x-auto"
                {...props}
            />
        );
    },
    pre: ({ node, ...props }) => (
        <pre className="bg-[rgba(0,0,0,0.06)] p-3 rounded my-2 overflow-x-auto" {...props} />
    ),
    blockquote: ({ node, ...props }) => (
        <blockquote
            className="border-l-4 border-[rgba(0,0,0,0.2)] pl-4 my-2 italic text-[rgba(0,0,0,0.65)]"
            {...props}
        />
    ),
    strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
    em: ({ node, ...props }) => <em className="italic" {...props} />,
};
